import { api } from './api';
import type { PaginatedResponse } from '@xdface-vault/shared';
import type {
  ProjectCreateInput,
  ProjectResponse,
  ProjectUpdateInput,
  ProjectQuestion,
  ProjectAttachmentMeta,
  DiagramKit,
} from '@xdface-vault/shared';

export interface ProjectWithFiles extends ProjectResponse {
  files?: { path: string; bytes?: number }[];
}

export interface ProjectFileData {
  path: string;
  content: string;
  updatedAt: string;
  version: number;
}

export interface ListProjectsParams {
  limit?: number;
  cursor?: string;
  companyId?: string;
  status?: string;
  dueBefore?: string;
  dueAfter?: string;
}

export const projectsApi = {
  list(params?: ListProjectsParams): Promise<PaginatedResponse<ProjectResponse>> {
    const query: Record<string, string> = {};
    if (params?.limit) query.limit = String(params.limit);
    if (params?.cursor) query.cursor = params.cursor;
    if (params?.companyId) query.companyId = params.companyId;
    if (params?.status) query.status = params.status;
    if (params?.dueBefore) query.dueBefore = params.dueBefore;
    if (params?.dueAfter) query.dueAfter = params.dueAfter;
    return api.get('/projects', query);
  },

  create(input: ProjectCreateInput): Promise<ProjectResponse> {
    return api.post('/projects', input);
  },

  get(id: string): Promise<ProjectWithFiles> {
    return api.get(`/projects/${id}`);
  },

  update(id: string, input: ProjectUpdateInput): Promise<ProjectResponse> {
    return api.put(`/projects/${id}`, input);
  },

  delete(id: string): Promise<void> {
    return api.delete(`/projects/${id}`);
  },

  // Files
  listFiles(id: string): Promise<{ files: { path: string; bytes?: number }[] }> {
    return api.get(`/projects/${id}/files`);
  },

  getFile(id: string, path: string): Promise<ProjectFileData> {
    return api.get(`/projects/${id}/files/${encodeURIComponent(path)}`);
  },

  putFile(
    id: string,
    path: string,
    content: string,
    expectedVersion: number,
    expectedUserId?: string,
  ): Promise<ProjectResponse> {
    return api.put(
      `/projects/${id}/files/${encodeURIComponent(path)}`,
      {
        content,
        expectedVersion,
      },
      ...(expectedUserId ? [expectedUserId] : []),
    );
  },

  deleteFile(id: string, path: string, expectedVersion: number): Promise<ProjectResponse> {
    return api.delete(`/projects/${id}/files/${encodeURIComponent(path)}`, { expectedVersion });
  },

  // Questions
  listQuestions(id: string): Promise<{ questions: ProjectQuestion[] }> {
    return api.get(`/projects/${id}/questions`);
  },

  addQuestion(
    id: string,
    input: { expectedVersion: number; title: string; notes?: string },
  ): Promise<ProjectResponse> {
    return api.post(`/projects/${id}/questions`, input);
  },

  updateQuestion(
    id: string,
    questionId: string,
    input: {
      expectedVersion: number;
      status?: 'open' | 'clarified';
      title?: string;
      notes?: string;
    },
  ): Promise<ProjectResponse> {
    return api.put(`/projects/${id}/questions/${questionId}`, input);
  },

  deleteQuestion(
    id: string,
    questionId: string,
    expectedVersion: number,
  ): Promise<ProjectResponse> {
    return api.delete(`/projects/${id}/questions/${questionId}`, { expectedVersion });
  },

  // Diagrams
  listDiagrams(
    id: string,
  ): Promise<{ diagrams: Array<{ id: string; title: string; pageCount: number }> }> {
    return api.get(`/projects/${id}/diagrams`);
  },

  linkDiagram(id: string, diagramId: string, expectedVersion: number): Promise<ProjectResponse> {
    return api.post(`/projects/${id}/diagrams`, { diagramId, expectedVersion });
  },

  createDiagram(
    id: string,
    title: string,
    expectedVersion: number,
    kit?: DiagramKit,
  ): Promise<{ diagramId: string; title: string; kit: DiagramKit }> {
    return api.post(`/projects/${id}/diagrams/new`, { title, kit, expectedVersion });
  },

  unlinkDiagram(id: string, diagramId: string, expectedVersion: number): Promise<ProjectResponse> {
    return api.delete(`/projects/${id}/diagrams/${diagramId}`, { expectedVersion });
  },

  // Attachments
  listAttachments(id: string): Promise<{ attachments: ProjectAttachmentMeta[] }> {
    return api.get(`/projects/${id}/attachments`);
  },

  presignAttachment(
    id: string,
    filename: string,
    contentType: string,
  ): Promise<{ uploadUrl: string; filename: string }> {
    return api.post(`/projects/${id}/attachments/presign`, { filename, contentType });
  },

  confirmAttachment(
    id: string,
    input: {
      expectedVersion: number;
      filename: string;
      bytes: number;
      contentType: string;
      note?: string;
    },
  ): Promise<ProjectResponse> {
    return api.post(`/projects/${id}/attachments`, input);
  },

  getAttachmentUrl(id: string, attachmentId: string): Promise<{ url: string }> {
    return api.get(`/projects/${id}/attachments/${attachmentId}/url`);
  },

  deleteAttachment(
    id: string,
    attachmentId: string,
    expectedVersion: number,
  ): Promise<ProjectResponse> {
    return api.delete(`/projects/${id}/attachments/${attachmentId}`, { expectedVersion });
  },

  deleteAttachmentByFilename(
    id: string,
    filename: string,
    expectedVersion: number,
  ): Promise<ProjectResponse> {
    return api.delete(`/projects/${id}/attachments/by-filename/${encodeURIComponent(filename)}`, {
      expectedVersion,
    });
  },
};
